"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { UserPlus } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import DarkModeToggle from "../importable/DarkmodeToggle"
import { useAuth } from "../../contexts/AuthContext"
import userService from "../../services/users/userService"
import authService from "../../services/authService"
import { AccountSwitcher } from "../ui/account-switcher"
import AlertButton from "../AlertStock/AlertButton"

// Define interface for local storage user data
interface LocalStorageUser {
    id: number;
    name: string;
    role: string;
    [key: string]: any; // For any other properties that might be present
}

export function Header() {
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false)
    const [isVisible, setIsVisible] = useState(false)
    const [isHovering, setIsHovering] = useState(false)
    const [showAccountSwitcher, setShowAccountSwitcher] = useState(false)
    const [currentUser, setCurrentUser] = useState<{ name: string; id: number; role: string } | null>(null)
    const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null)
    const { logout } = useAuth()

    // Fetch current user data from both API and local storage
    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                // First try to get user from local storage directly using the correct key
                const authUserJson = localStorage.getItem('auth_user')
                let localStorageUser: LocalStorageUser | null = null
                
                if (authUserJson) {
                    try {
                        localStorageUser = JSON.parse(authUserJson) as LocalStorageUser
                    } catch (error) {
                                            }
                }
                
                // If we have a user from local storage, use it as a base
                if (localStorageUser && localStorageUser.id) {
                    // Set current user with data from local storage
                    setCurrentUser({
                        id: localStorageUser.id,
                        name: localStorageUser.name || 'User',
                        role: localStorageUser.role || 'admin'
                    })
                    
                    // Try to get additional profile data from API
                    try {
                        const userData = await userService.getUser(localStorageUser.id)
                        
                        // Update current user with additional API data while keeping the role from local storage
                        const userRole = localStorageUser.role || 'admin'
                        setCurrentUser(prevUser => ({
                            ...userData,
                            role: userRole // Ensure role from local storage is preserved
                        }))
                        
                        // Set profile image URL if available
                        if (userData.profile) {
                            setProfileImageUrl(userData.profile)
                        }
                    } catch (apiError) {
                                                // Already set currentUser from local storage, so just log the error
                    }
                } else {
                    // Fallback to authService if direct local storage access failed
                    const authUser = authService.getUser()
                    if (authUser && authUser.id) {
                        try {
                            const userData = await userService.getUser(authUser.id)
                            setCurrentUser({
                                ...userData,
                                role: authUser.role || 'admin'
                            })
                            
                            if (userData.profile) {
                                setProfileImageUrl(userData.profile)
                            }
                        } catch (error) {
                                                        // Set basic user info from authService
                            setCurrentUser({
                                id: authUser.id,
                                name: authUser.username || authUser.name || 'User',
                                role: authUser.role || 'admin'
                            })
                        }
                    }
                }
            } catch (error) {
                            }
        }

        fetchCurrentUser()
    }, [])

    // Close dropdowns when header visibility changes
    useEffect(() => {
        if (!isVisible) {
            setIsUserDropdownOpen(false)
        }
    }, [isVisible])

    useEffect(() => {
        let timeoutId: NodeJS.Timeout

        const handleMouseMove = (e: MouseEvent) => {
            if (e.clientY <= 60 || isHovering) {
                setIsVisible(true)
                if (timeoutId) {
                    clearTimeout(timeoutId)
                }
            } else {
                // Only start hiding timeout if we're not hovering
                if (!isHovering) {
                    timeoutId = setTimeout(() => {
                        setIsVisible(false)
                    }, 300) // Reduced timeout for smoother transition
                }
            }
        }

        window.addEventListener("mousemove", handleMouseMove)

        return () => {
            window.removeEventListener("mousemove", handleMouseMove)
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
        }
    }, [isHovering])

    // Force visible state when hovering
    useEffect(() => {
        if (isHovering) {
            setIsVisible(true)
        }
    }, [isHovering])

    const toggleUserDropdown = () => {
        setIsUserDropdownOpen(!isUserDropdownOpen)
    }

    // Handler for sign out
    const handleSignOut = (e: React.MouseEvent) => {
        e.preventDefault()
        logout() // Call the logout function from AuthContext
        setIsUserDropdownOpen(false) // Close the dropdown after logout
    }

    // Handler for switch account
    const handleSwitchAccount = (e: React.MouseEvent) => {
        e.preventDefault()
        setIsUserDropdownOpen(false) // Close the dropdown
        setShowAccountSwitcher(true) // Show the account switcher modal
    }

    // Get the first letter of the user's name for the avatar
    const getUserInitial = () => {
        if (currentUser && currentUser.name) {
            return currentUser.name.charAt(0).toUpperCase()
        }
        return "U" // Default if no name is available
    }

    // Get the user role directly from local storage to ensure it's always available
    const getUserRole = (): string => {
        try {
            const authUserJson = localStorage.getItem('auth_user')
            if (authUserJson) {
                const userData = JSON.parse(authUserJson) as LocalStorageUser
                return userData.role || 'admin'
            }
        } catch (error) {
                    }
        
        // If we couldn't get the role from auth_user, try from currentUser
        if (currentUser && currentUser.role) {
            return currentUser.role
        }
        
        return 'admin' // Default fallback
    }

    // Get the profile route based on user role from local storage
    const getProfileRoute = () => {
        const role = getUserRole().toLowerCase()
        
        switch (role) {
            case "admin":
            case "manager":
                return "/admin/profile"
            case "cashier":
                return "/cashier/profile"
            case "comptable":
                return "/comptable/profile"
            default:
                return "/admin/profile"
        }
    }

    // Get the settings route based on user role
    const getSettingsRoute = () => {
        const role = getUserRole().toLowerCase()
        
        // Only admin has access to settings in this case
        if (role === "admin") {
            return "/admin/settings"
        }
        
        // For other roles, redirect to profile
        return getProfileRoute()
    }

    // Determine if settings should be shown based on role
    const shouldShowSettings = () => {
        const role = getUserRole().toLowerCase()
        return role === "admin"
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement

            // Check if click is outside user dropdown
            if (isUserDropdownOpen && !target.closest('[data-dropdown="user"]')) {
                setIsUserDropdownOpen(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [isUserDropdownOpen])

    return (
        <>
            <header
                className={`fixed top-2 left-1/2 transform -translate-x-1/2 z-50 transition-all w-full duration-300 ease-in-out ${
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full"
                }`}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => {
                    setIsHovering(false)
                    // Only hide if mouse is not near the top
                    if (document.documentElement.clientHeight > 60) {
                        setIsVisible(false)
                    }
                }}
            >
                <div className="max-w-4xl w-full mx-auto bg-white dark:bg-neutral-950 rounded-xl border border-gray-200 dark:border-neutral-800 shadow-lg">
                    <div className="px-8">
                        <div className="flex h-16 items-center justify-between">
                            <div className="flex items-center gap-8">
                                <Link to="/" className="flex items-center">
                                    <span className="font-bold text-2xl text-primary">POS</span>
                                    <span className="font-bold text-2xl text-neutral-600 dark:text-neutral-300">nex</span>
                                </Link>
                            </div>

                            <div className="flex items-center gap-4">
                                <AlertButton className="" />

                                <DarkModeToggle />

                                <div className="relative" data-dropdown="user">
                                    <button
                                        onClick={toggleUserDropdown}
                                        className="flex items-center justify-center h-8 w-8 rounded-full overflow-hidden bg-primary-hover text-white font-medium"
                                        aria-label="Menu utilisateur"
                                    >
                                        {profileImageUrl ? (
                                            <img
                                                src={profileImageUrl || "/placeholder.svg"}
                                                alt="Profil"
                                                className="h-full w-full object-cover"
                                                onError={() => setProfileImageUrl(null)} // Fall back to initials if image fails to load
                                            />
                                        ) : (
                                            getUserInitial()
                                        )}
                                    </button>
                                    <AnimatePresence>
                                        {isUserDropdownOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                                transition={{ duration: 0.2 }}
                                                className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-neutral-50 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 ring-1 ring-black ring-opacity-5"
                                            >
                                                <div className="rounded-md" role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
                                                    <Link
                                                        to={getProfileRoute()}
                                                        className="block px-4 py-2 text-sm text-gray-700 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-700"
                                                        role="menuitem"
                                                    >
                                                        Votre Profil
                                                    </Link>
                                                    {shouldShowSettings() && (
                                                        <Link
                                                            to={getSettingsRoute()}
                                                            className="block px-4 py-2 text-sm text-gray-700 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-700"
                                                            role="menuitem"
                                                        >
                                                            Paramètres
                                                        </Link>
                                                    )}
                                                    <button
                                                        onClick={handleSwitchAccount}
                                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-700 border-t border-neutral-200 dark:border-neutral-700"
                                                        role="menuitem"
                                                    >
                                                        <div className="flex items-center">
                                                            <UserPlus className="h-4 w-4 mr-2" />
                                                            Changer de Compte
                                                        </div>
                                                    </button>
                                                    <button
                                                        onClick={handleSignOut}
                                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-700 border-t border-neutral-200 dark:border-neutral-700"
                                                        role="menuitem"
                                                    >
                                                        Se déconnecter
                                                    </button>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Account Switcher Modal */}
            {showAccountSwitcher && (
                <AccountSwitcher onClose={() => setShowAccountSwitcher(false)} currentUserProfile={profileImageUrl} />
            )}
        </>
    )
}